import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { api } from '../lib/api'
import { CERT_LABEL, makeLabeler, useCommonCodes, useTargetCodes } from '../lib/codes'

/**
 * 기업카드 목록 — 화면 명칭은 과업지시서 §3-가의 "기업카드"를 유지한다(용어 통일표 §0-2).
 * 데이터 이름은 `해결카드`(`solution_card`)이고 둘이 다른 것은 의도된 것이다.
 *
 * 필터 4종은 과업지시서 §3-가가 요구한 카테고리·대상·문제영역·제공자원에 대응한다.
 */
export function SolutionListPage() {
  const [category, setCategory] = useState('')
  const [target, setTarget] = useState('')
  const [resource, setResource] = useState('')
  const [serviceArea, setServiceArea] = useState('')
  const [q, setQ] = useState('')

  const cards = useQuery({
    queryKey: ['solution-cards', { category, target, resource, serviceArea, q }],
    queryFn: () => api.solutionCards({ category, target, resource, serviceArea, q, limit: 60 }),
  })

  const categories = useCommonCodes('category')
  const targets = useTargetCodes()
  const resources = useCommonCodes('resource')
  const areas = useCommonCodes('service_area')

  const categoryLabel = makeLabeler(categories.data)
  const resourceLabel = makeLabeler(resources.data)

  return (
    <>
      <h1 className="page-title">기업해결카드</h1>
      <p className="page-desc">
        지역 문제를 해결할 수 있는 기업의 해결카드입니다. 한 기업이 여러 개의 카드를 가질 수
        있습니다.
      </p>

      <div className="filters">
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="분야">
          <option value="">분야 전체</option>
          {categories.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="대상">
          <option value="">대상 전체</option>
          {targets.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select value={resource} onChange={(e) => setResource(e.target.value)} aria-label="제공 자원">
          <option value="">제공 자원 전체</option>
          {resources.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          value={serviceArea}
          onChange={(e) => setServiceArea(e.target.value)}
          aria-label="서비스 지역"
        >
          <option value="">서비스 지역 전체</option>
          {areas.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="제목·요약 검색"
          aria-label="검색어"
        />
      </div>

      <QueryState
        isPending={cards.isPending}
        error={cards.error}
        isEmpty={cards.data?.items.length === 0}
        emptyMessage="조건에 맞는 기업카드가 없습니다."
      >
        <p className="muted small" style={{ marginBottom: 12 }}>
          {cards.data?.total}개 카드
        </p>
        <div className="grid">
          {cards.data?.items.map((c) => (
            <Link key={c.id} className="card" to={`/solutions/${c.id}`}>
              <div className="card-org">
                {c.org.name}
                {c.org.certType && CERT_LABEL[c.org.certType] ? (
                  <>
                    {' '}
                    <span className={c.org.certType === 'cert_certified' ? 'badge cert' : 'badge pre'}>
                      {CERT_LABEL[c.org.certType]}
                    </span>
                  </>
                ) : null}
              </div>
              <h3>{c.title}</h3>
              <p>{c.summary}</p>
              <div className="tags">
                {c.categoryCodes.map((code) => (
                  <span key={code} className="tag">
                    {categoryLabel(code)}
                  </span>
                ))}
                {c.resourceCodes.map((code) => (
                  <span key={code} className="tag">
                    {resourceLabel(code)}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </QueryState>
    </>
  )
}
